import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment/min/moment-with-locales';
import { Modal, OverlayTrigger, Tooltip, Col } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import applicationRouter from '~/hoc/applicationRouter';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import withLocalization from '~/hoc/withLocalization';
import EditTimesheetTimeModal from './EditTimesheetTimeModal';
import UserPromptModal from '../../../components/GenericModal/userPromptModal/index.js';

// Elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { shortDateFormat, timeFormat } from '~/utils/dateFormat';
import customerFeature from '~/utils/customerFeature';
import { statusMap } from '~/utils/normalStatuses';

//assets
import Edit from '../../../assets/img/editing.svg';
import Delete from '../../../assets/img/deleting.svg';
import cancelIcon from '../../../assets/img/InactiveUser.svg';
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

class TimelogConflictsModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isLoading: false,
            showEditTimeModal: false,
            editTimelogId: null,
            editTimelogData: null,
            userPromptModal: {
                source: '',
                header: '',
                showModal: false,
                sourceInfo: {},
                actionMethods: {}
            }
        }
    };

    handleCloseEditTimeModal = () => {
        this.setState({ showEditTimeModal: false, editTimelogId: null, editTimelogData: null });
    }

    handleCloseUserPromptModal = () => {
        this.setState({ userPromptModal: { ...this.state.userPromptModal, showModal: false } });
    }

    handleAutoTimesheetPromptOnLeaveRemoval = (prompt, retryWithDecision) => {
        this.setState({
            userPromptModal: {
                ...prompt,
                actionMethods: {
                    handleCloseUserPromptModal: this.handleCloseUserPromptModal,
                    handleAllowAutoTimesheet: () => {
                        this.handleCloseUserPromptModal();
                        return retryWithDecision({ autoRecordTimesheet: true });
                    },
                    handleDisableAutoTimesheet: () => {
                        this.handleCloseUserPromptModal();
                        return retryWithDecision({ autoRecordTimesheet: false });
                    }
                }
            }
        });
    };

    confirmAbsenceCancellation = (id) => {
        const { conflicts } = this.props;
        return confirmAlert({
            title: this.props.t("Absence Cancellation"),
            message: `${this.props.t("Are you sure you want to cancel this request, it cannot be changed later")}?`,
            buttons: [
                {
                    label: this.props.t('Yes'),
                    onClick: () => this.cancelAbsenceConflictWithAutoTimesheetCheck(id, conflicts),
                },
                {
                    label: this.props.t('No'),
                    onClick: () => { },
                },
            ],
        });
    }

    cancelAbsenceConflictWithAutoTimesheetCheck = (id, conflicts, decisionData) => {
        return this.props.absenceStore.cancelAbsence(id, decisionData).then((res) => {
            const prompt = res?.userPrompts?.find(p => p.source === 'autoTimesheetOnLeaveRemoval');
            if (prompt?.showModal) {
                return this.handleAutoTimesheetPromptOnLeaveRemoval(prompt, (decision) => this.cancelAbsenceConflictWithAutoTimesheetCheck(id, conflicts, decision));
            }
            this.props.commonStore.addNotification(this.props.t(res.message), null, 'success')
            this.props.handleShowConflict(conflicts.allTimelogs[0], false).then(() => {
                this.props.onSaved?.();
            });
        }).catch((err) => {
            this.props.commonStore.addNotification(this.props.t(err), null, 'error');
        });
    }

    handleTableButtonActionAbsence = (id, type, isSelfAbsence) => {
        const { conflicts } = this.props;
        if (type === 'edit') this.props.router.navigate(`/admin/absences/${id}`,
            { state: { absence: id } });
        else if (type === 'loadCompOff')
            this.setState({ showCompOffForm: true, entity_id: id })
        else if (type === 'cancel') {
            this.confirmAbsenceCancellation(id);
        }
        else if (type === 'remove') {
            return this.props.handleDelete(id, (id) => this.removeAbsenceConflictWithAutoTimesheetCheck(id, conflicts));
        }
    };

    removeAbsenceConflictWithAutoTimesheetCheck = (id, conflicts, decisionData) => {
        return this.props.absenceStore.remove(id, decisionData).then((res) => {
            const prompt = res?.userPrompts?.find(p => p.source === 'autoTimesheetOnLeaveRemoval');
            if (prompt?.showModal) {
                return this.handleAutoTimesheetPromptOnLeaveRemoval(prompt, (decision) => this.removeAbsenceConflictWithAutoTimesheetCheck(id, conflicts, decision));
            }
            this.props.handleShowConflict(conflicts.allTimelogs[0], false).then(() => {
                this.props.onSaved?.();
            });
            return res;
        });
    };

    renderTableActionButtonsAbsenceConflicts = (id, full) => {
        const { t, userStore } = this.props;
        const { user_type, id: user_id } = userStore.currentUser;
        const { status, absence_type } = full;
        const allow = (user_type === 'pm' || user_type === 'admin') && customerFeature('pm_allow_editing_all');
        let statusText = status;
        if (!statusText) statusText = 'active';
        statusText = statusMap[statusText] || '';
        const isSelfAbsence = full.user_id === user_id;
        return (
            <div>
                {status === 'approved' ?
                    <div className="actions-center">
                        {customerFeature('allow_accepting') && (
                            <div
                                className={`status-block status-${status || 'active'} 
                        ${user_type === 'member' ? ' status-no-click-for-member' : ''}`}
                            >
                                <div className="text-capitalize">{t(statusText)}</div>
                            </div>
                        )}
                    </div> :
                    <div className="actions-center">
                        <OverlayTrigger
                            key="absence_edit"
                            placement="top"
                            overlay={
                                <Tooltip id="tooltip-top">
                                    {t('Edit Absence')}
                                </Tooltip>
                            }
                        >
                            <Button icon_sm fill
                                disabled={status !== 'active'}
                                onClick={() => absence_type === 'timebank' ? this.handleTableButtonActionAbsence(id, 'loadCompOff') : this.handleTableButtonActionAbsence(id, 'edit', isSelfAbsence)}>
                                <img src={Edit} alt={'edit absence'} />
                            </Button>
                        </OverlayTrigger>

                        {user_type !== 'admin' &&
                            <OverlayTrigger
                                key="absence_cancel"
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t('Cancel Absence')}
                                    </Tooltip>
                                }
                            >
                                <Button icon_sm fill
                                    disabled={!isSelfAbsence || status !== 'active'}
                                    onClick={() => this.handleTableButtonActionAbsence(id, 'cancel', isSelfAbsence)}>
                                    <img src={cancelIcon} alt={'cancel absence'} />
                                </Button>
                            </OverlayTrigger>
                        }

                        {allow &&
                            <OverlayTrigger
                                key="absence_delete"
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t('Remove Absence')}
                                    </Tooltip>
                                }
                            >
                                <Button icon_sm_delete fill
                                    disabled={status === 'approved' && (!full.is_imported || user_type !== 'admin')}
                                    onClick={() => this.handleTableButtonActionAbsence(id, 'remove', isSelfAbsence)}>
                                    <img src={Delete} alt="delete absence" />
                                </Button>
                            </OverlayTrigger>
                        }
                    </div>
                }
            </div>
        );
    };

    handleTableButtonActionTimesheet = (full, type) => {
        const { id } = full;
        const { conflicts } = this.props;

        if (type === 'edit') {
            this.setState({ showEditTimeModal: true, editTimelogId: id, editTimelogData: full });
        }
        if (type === 'remove') {
            return this.props.handleDelete(id, (id) => {
                return this.props.timelogStore.remove(id).then((res) => {
                    this.props.handleShowConflict(conflicts.allTimelogs[0], false).then(() => {
                        this.props.onSaved?.();
                    });
                    return res;
                });
            });
        }
    };

    handleMismatchStatus = (full) => {
        const { t, commonStore } = this.props;
        this.setState({
            showMultiTaskModalAccept: true, selectedUserId: full.user_id, selectedForDate: full.for_date
        }, () => {
            commonStore.addNotification(t('Please reset all timelogs before making other changes.'), null, 'warning');
        });
    }

    renderTableActionButtonsTimesheetConflicts = (id, full) => {
        const { t, commonStore, timelogStore } = this.props;
        const { config } = commonStore;
        const { user_type, id: userId } = this.props.userStore.currentUser;
        const { currentMultiTaskTimeLogs } = timelogStore;
        const hideEditWhenAccepted = customerFeature('timelog_no_edit_for_approved');
        const hideEditWhenNotDraft = customerFeature('timelog_no_edit_for_done');
        let hideEdit = hideEditWhenAccepted && full.status === 'approved' && user_type === 'member';
        if (hideEditWhenNotDraft && full.status !== 'draft' && user_type === 'member') {
            hideEdit = true;
        }
        const allow = (user_type === 'pm' || user_type === 'admin') && customerFeature('pm_allow_editing_all');

        let statusText = full.status;
        if (!statusText) statusText = 'active';
        statusText = statusMap[statusText] || '';

        const isStatusMismatched = currentMultiTaskTimeLogs && currentMultiTaskTimeLogs.length > 0 && !currentMultiTaskTimeLogs.every(log => log.status === currentMultiTaskTimeLogs[0].status) && !currentMultiTaskTimeLogs.some(log => log.status === 'draft');
        return (
            <div className="actions-center">
                {full.has_conflicts && (
                    <div>
                        <OverlayTrigger key="reset-btn" placement='top'
                            overlay={<Tooltip id='tooltip-conflicts'>
                                {t('Conflicts')}
                            </Tooltip>}
                        >
                            <Button icon_sm fill
                                onClick={() => { }}>
                                {<i className="fa fa-exclamation text-danger"></i>}
                            </Button>
                        </OverlayTrigger>
                    </div>
                )}
                {statusText === 'approved' ?
                    <div className="actions-center">
                        {customerFeature('allow_accepting') && (
                            <div
                                className={`status-block status-${statusText || 'active'} 
                        ${user_type === 'member' ? ' status-no-click-for-member' : ''}`}
                            >
                                <div className="text-capitalize">{t(statusText)}</div>
                            </div>
                        )}
                    </div> :
                    <>
                        {!hideEdit &&
                            <OverlayTrigger
                                key="timelog_edit"
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t('Edit Timesheet')}
                                    </Tooltip>
                                }
                            >
                                <Button icon_sm fill
                                    onClick={() => isStatusMismatched && user_type !== 'member'
                                        ? this.handleMismatchStatus(full)
                                        : (full.ismultitasklog ? this.handleOpenMultiTaskModal(full.user_id, full.for_date) : this.handleTableButtonActionTimesheet(full, 'edit'))}>
                                    {(userId !== full.user_id && !allow) || full.ismultitasklog ? <i className="fa fa-eye" /> : <img src={Edit} alt={'edit'} />}
                                </Button>
                            </OverlayTrigger>
                        }
                        {(userId === full.user_id || allow) && (
                            <OverlayTrigger
                                key="timelog_delete"
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {full.status === 'approved' && config.integration_details.product ?
                                            t('Please reset approved hours before deleting them permanently from Time & Control')
                                            : t('Delete Timesheet')
                                        }.
                                    </Tooltip>
                                }
                            >
                                <Button
                                    icon_sm_delete
                                    fill
                                    onClick={() => isStatusMismatched && user_type !== 'member'
                                        ? this.handleMismatchStatus(full)
                                        : (full.ismultitasklog ? this.handleOpenMultiTaskModal(full.user_id, full.for_date) : this.handleTableButtonActionTimesheet(full, 'remove'))}
                                    disabled={(full.status === 'approved' && config.integration_details.product)}
                                >
                                    <img src={Delete} alt="delete" />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </>
                }
            </div>
        );
    };

    render() {
        if (this.state.isLoading)
            return <LoadingSpinner />;
        const { showModal, conflicts, handleCloseConflictsModal, t } = this.props;
        const { config } = this.props.commonStore;
        const dateTimeRules = config?.client?.data?.dateTimeRules ? config.client.data.dateTimeRules : false;
        const { userPromptModal } = this.state;

        return (
            <div>
                <Modal
                    className={`modal right fade modal_revised`}
                    show={showModal}
                    onHide={handleCloseConflictsModal}
                    centered>
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center ApproveModal__title m-0">
                            {this.props.t(`${t("Timesheet Conflicts")}`)}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt='close icon'
                            onClick={handleCloseConflictsModal}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row timelog-grid-time-card mt-2">
                            <Col xs={12} md={6}
                                className="timelog-grid-item-1 justify-content-space-between card-timelog-grid-item"
                            >
                                <div className="d-flex">
                                    <i className="timelog-grid__icon"></i>
                                    <span className="timelog-grid__text" style={{ width: 'fit-content', textTransform: 'capitalize' }}>
                                        {t('Name')}
                                    </span>
                                </div>
                                <b className="timelog-final-time-card-modal align-self-center">
                                    {conflicts?.userInfo?.first_name} {conflicts?.userInfo?.last_name}
                                </b>
                            </Col>
                            <Col xs={12} md={6}
                                className="timelog-grid-item-2 justify-content-space-between card-timelog-grid-item"
                            >
                                <div className="d-flex align-items-center">
                                    <i className="timelog-grid__icon"></i>
                                    <span className="timelog-grid__text">{t('For Date')}</span>
                                </div>
                                <b className="timelog-final-time-card-modal align-self-center">
                                    {conflicts?.allTimelogs[0]?.status === 'active' && conflicts?.allTimelogs[0]?.from > conflicts?.allTimelogs[0]?.to ?
                                        `${shortDateFormat(conflicts?.allTimelogs[0]?.for_date, dateTimeRules)} | ${shortDateFormat(moment(conflicts?.allTimelogs[0]?.for_date).add(1, 'days'), dateTimeRules)}` :
                                        shortDateFormat(conflicts?.allTimelogs[0]?.for_date, dateTimeRules)
                                    }
                                </b>
                            </Col>
                        </div>

                        {/* NO CONFLICTS */}
                        {!(conflicts?.overlappingAbsence?.length ||
                            conflicts?.overlappingTimelogs?.length) ? (
                            <div
                                className="card border-0 shadow-sm text-center p-4"
                                style={{
                                    borderRadius: "16px",
                                    backgroundColor: "#f1f8f4"
                                }}
                            >
                                <h5 className="fw-bold text-success m-0">
                                    {t('All Conflicts Resolved')}
                                </h5>
                            </div>
                        ) :
                            <div>
                                <div className="alert alert-info text-left mb-1 fw-bold rounded-2">
                                    <i className="fa fa-circle-info fs16 me-2"></i> <span className='fs15 fw-bold'>{t("Some timesheet entries overlap with other timesheets or absences. To resolve this conflict, please edit or delete the overlapping entries below.")}</span>
                                </div>
                                <div
                                    className="alert text-left mb-4 fw-bold rounded-2"
                                    style={{ backgroundColor: "#f3a362ff", borderColor: "#f3a362ff", color: "#fff" }}
                                >
                                    <i className="fa fa-circle-info fs16 me-2"></i>
                                    <span className='fs15 fw-bold'>
                                        {t("Timesheet cannot be approved or sync with payroll if its conflicted.")}
                                    </span>
                                </div>

                                <div className="row g-3">
                                    {/* LEFT COLUMN */}
                                    {conflicts?.conflictedTimelog?.length > 0 && (
                                        <div className="col-md-6">

                                            <div className="card shadow-sm border-0" style={{ borderRadius: "16px", backgroundColor: "#f7d4d4ff", }}>
                                                <div className="card-body">

                                                    <div className="text-danger fw-bold fs-16 text-center mb-2">
                                                        {t('Conflicted Timesheet')}
                                                    </div>

                                                    <div
                                                        className="table-responsive"
                                                        style={{
                                                            backgroundColor: "#fafbfc",
                                                            borderRadius: "12px",
                                                        }}
                                                    >
                                                        <div className="fw-semibold mx-2 text-info small">
                                                            {t('Timesheet')}
                                                        </div>
                                                        <table
                                                            className="table align-middle mb-0 mt-1"
                                                            style={{
                                                                backgroundColor: "#ffffff",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            <thead
                                                                style={{
                                                                    backgroundColor: "#eef2f7"
                                                                }}
                                                            >
                                                                <tr>
                                                                    <th className="fw-semibold">{t('Date')}</th>
                                                                    <th className="fw-semibold">{t('Start')}</th>
                                                                    <th className="fw-semibold">{t('End')}</th>
                                                                    <th className="fw-semibold text-center">{t('Actions')}</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {conflicts?.conflictedTimelog?.length > 0 && conflicts.conflictedTimelog
                                                                    .map(ele => (
                                                                        <tr
                                                                            key={ele.id}
                                                                        >
                                                                            <td>{ele.for_date}</td>
                                                                            <td>{timeFormat(ele.from, dateTimeRules)}</td>
                                                                            <td>{timeFormat(ele.to, dateTimeRules)}</td>
                                                                            <td className="text-center">
                                                                                {this.renderTableActionButtonsTimesheetConflicts(ele.id, ele)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                            </tbody>

                                                        </table>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    )}

                                    {/* RIGHT COLUMN */}
                                    <div className="col-md-6 d-flex flex-column gap-3">
                                        {(conflicts?.overlappingAbsence?.length || conflicts?.overlappingTimelogs?.length) && (
                                            <div className="card shadow-sm border-0" style={{ borderRadius: "16px", backgroundColor: "#f7d4d4ff", }}>
                                                <div className="card-body">

                                                    <div className="text-danger fw-bold fs-16 text-center mb-2">
                                                        {t('Conflicted With')}
                                                    </div>

                                                    {/* OVERLAPPING TIMELOGS */}
                                                    {conflicts?.overlappingTimelogs?.length > 0 && (
                                                        <div
                                                            className="table-responsive"
                                                            style={{
                                                                backgroundColor: "#fafbfc",
                                                                borderRadius: "12px",
                                                            }}
                                                        >
                                                            <div className="fw-semibold mx-2 text-info small">
                                                                {t('Timesheet')}
                                                            </div>
                                                            <table className="table align-middle mb-0 mt-1"
                                                                style={{ backgroundColor: "#ffffff", borderRadius: "10px" }}>
                                                                <thead style={{ backgroundColor: "#eef2f7" }}>
                                                                    <tr>
                                                                        <th className="fw-semibold">{t('Date')}</th>
                                                                        <th className="fw-semibold">{t('Start')}</th>
                                                                        <th className="fw-semibold">{t('End')}</th>
                                                                        <th className="fw-semibold text-center">{t('Actions')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {conflicts.overlappingTimelogs.map(ele => (
                                                                        <tr key={ele.id}>
                                                                            <td>{ele.for_date}</td>
                                                                            <td>{timeFormat(ele.from, dateTimeRules)}</td>
                                                                            <td>{timeFormat(ele.to, dateTimeRules)}</td>
                                                                            <td className="text-center">
                                                                                {this.renderTableActionButtonsTimesheetConflicts(ele.id, ele)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                    {/* Overlapping Absence */}
                                                    {conflicts?.overlappingAbsence?.length > 0 && (
                                                        <div
                                                            className="table-responsive"
                                                            style={{
                                                                backgroundColor: "#fafbfc",
                                                                borderRadius: "12px",
                                                                marginTop: '10px'
                                                            }}
                                                        >
                                                            <div className="fw-semibold mx-2 text-info small">
                                                                {t('Absence')}
                                                            </div>

                                                            <table className="table align-middle mb-0 mt-1"
                                                                style={{ backgroundColor: "#ffffff", borderRadius: "10px" }}>
                                                                <thead style={{ backgroundColor: "#eef2f7" }}>
                                                                    <tr>
                                                                        <th className="fw-semibold">{t('Date')}</th>
                                                                        <th className="fw-semibold">{t('Time')}</th>
                                                                        <th className="fw-semibold">{t('Total')}</th>
                                                                        <th className="fw-semibold text-center">{t('Actions')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {conflicts.overlappingAbsence.map(ele => (
                                                                        <tr key={ele.id}>
                                                                            <td>{ele.from} | {ele.to}</td>
                                                                            <td>
                                                                                {timeFormat(ele.start_time, dateTimeRules)} -{" "}
                                                                                {timeFormat(ele.end_time, dateTimeRules)}
                                                                            </td>
                                                                            <td>
                                                                                {ele.employement_grade < 100
                                                                                    ? `${ele.hours} H | ${ele.total_days} D`
                                                                                    : ele.total_days}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {this.renderTableActionButtonsAbsenceConflicts(ele.id, ele)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        }
                    </Modal.Body>
                </Modal>

                <EditTimesheetTimeModal
                    show={this.state.showEditTimeModal}
                    onHide={this.handleCloseEditTimeModal}
                    editTimelogId={this.state.editTimelogId}
                    editTimelogData={this.state.editTimelogData}
                    message={t('Please edit time to resolve conflicts')}
                    onSaved={(timelog) => {
                        this.props.handleShowConflict(conflicts.allTimelogs[0], false).then(() => {
                            this.props.onSaved?.();
                        });
                    }}
                />

                <UserPromptModal
                    source={userPromptModal.source}
                    header={userPromptModal.header}
                    showModal={userPromptModal.showModal}
                    sourceInfo={userPromptModal.sourceInfo}
                    actionMethods={userPromptModal.actionMethods}
                />
            </div >
        );
    }
}
export default inject(
    'timelogStore',
    'userStore',
    'commonStore',
    'absenceStore'
)(applicationRouter(withLocalization(withRemoveDialog(observer(TimelogConflictsModal)))));
